'use client';
// « Copier le lien » — le geste qui rend l'adressage utile.
//
// Adresser les fiches ne sert à rien si l'on ne peut pas ATTRAPER l'adresse.
// La barre du navigateur la porte, mais personne ne va la chercher : on
// travaille dans l'application, pas dans le chrome du navigateur. Ce bouton
// met le lien complet dans le presse-papier, prêt à coller dans un courriel ou
// un message.
//
// ── Ce qu'il copie, et pourquoi ce n'est pas l'URL courante ────────────────
//
// Il RECONSTRUIT le lien depuis la vue et la référence, au lieu de recopier
// `window.location.href`. Deux raisons :
//
//  · une fiche ouverte EN PANNEAU superposé n'a pas changé l'adresse (c'est
//    voulu, cf. `useAdresse`) : copier l'URL courante enverrait le
//    destinataire sur l'écran de dessous, pas sur la fiche ;
//  · l'adresse peut porter une recherche ou un filtre en query — du contexte
//    privé qui n'a aucune raison de partir dans un courriel.
//
// ── Le retour, et son délai ────────────────────────────────────────────────
//
// Copier est une action sans effet visible : sans confirmation, on clique deux
// fois, puis on va vérifier dans la barre d'adresse. Le libellé change donc
// pendant deux secondes. Et si le presse-papier est refusé (contexte non
// sécurisé, permission navigateur), le lien est AFFICHÉ pour être sélectionné
// à la main — un échec muet renverrait au même problème qu'avant.
import React, { useState } from 'react';
import { cheminDe } from '@/lib/adresse';
import { I } from '@/components/ui';
import type { GedView } from '@/lib/types';

import { Bouton } from '@/shared/Bouton';
export function LienFiche({ vue, reference, quoi }: {
  vue: GedView;
  /** Référence LISIBLE — c'est elle qui voyage, jamais l'identifiant interne. */
  reference: string;
  /** Nommé dans l'infobulle : « le lien vers cette non-conformité ». */
  quoi?: string;
}) {
  const [etat, setEtat] = useState<'repos' | 'copie' | 'refus'>('repos');
  if (!reference) return null;
  const lien = typeof window === 'undefined'
    ? cheminDe(vue, reference)
    : `${window.location.origin}${cheminDe(vue, reference)}`;

  const copier = async () => {
    try {
      await navigator.clipboard.writeText(lien);
      setEtat('copie');
      setTimeout(() => setEtat('repos'), 2000);
    } catch {
      setEtat('refus');
    }
  };

  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 4 }}>
      <Bouton taille="sm" type="button" onClick={() => void copier()} title={`Copier le lien vers ${quoi || 'cette fiche'} — à coller dans un courriel ou un message`}>
        <I n={etat === 'copie' ? 'check' : 'link'} s={12} />
        {etat === 'copie' ? 'Lien copié' : 'Copier le lien'}
      </Bouton>
      {etat === 'refus' && (
        <input readOnly value={lien} onFocus={(e) => e.currentTarget.select()}
          aria-label="Lien à copier à la main"
          style={{ fontFamily: 'var(--font-mono)', fontSize: 11, padding: '4px 6px', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', width: 260 }} />
      )}
    </span>
  );
}
